import {
  type ColumnDef,
  getCoreRowModel,
  getSortedRowModel,
  type RowSelectionState,
  type SortingState,
  type Updater,
  useReactTable,
  type VisibilityState,
} from '@tanstack/react-table';
import { Download } from 'lucide-react';
import { type ReactNode, useMemo, useState } from 'react';
import { cx } from '@/lib/cx';
import { useElementWidth, useLocalStorage } from '@/lib/hooks';
import { t, tp } from '@/lib/i18n';
import { Checkbox } from '../Checkbox';
import { IconButton } from '../IconButton';
import { EmptyState, ErrorState } from '../State';
import { CardList } from './CardList';
import { ColumnChooser } from './ColumnChooser';
import { downloadCsv, toCsv } from './csv';
import s from './DataTable.module.css';
import { TableView } from './TableView';
import { type DataColumn, formatSort, parseSort, type SortParam } from './types';

export interface DataTableProps<T> {
  /** Stable id: column choices are remembered per table in this browser. */
  id: string;
  caption: string;
  data: T[];
  columns: DataColumn<T>[];
  getRowId: (row: T) => string;
  /** Sort bound to the URL ("name" / "-name"); uncontrolled when omitted. */
  sort?: SortParam;
  onSortChange?: (sort: SortParam) => void;
  /** The server sorts (`data` arrives in `sort` order, page by page): the table only shows it.
   * Mark the columns the server cannot sort by `sortable: false`. */
  serverSort?: boolean;
  onRowClick?: (row: T) => void;
  activeRowId?: string;
  selectable?: boolean;
  selection?: string[];
  onSelectionChange?: (ids: string[]) => void;
  /** Bulk actions shown while rows are selected. */
  bulkActions?: (ids: string[]) => ReactNode;
  loading?: boolean;
  error?: unknown;
  onRetry?: () => void;
  empty?: ReactNode;
  hasMore?: boolean;
  onLoadMore?: () => void;
  /** Enables CSV export of the loaded rows (visible order, accessor values). */
  csvName?: string;
  /** Container width below which rows render as cards (default 640 px). */
  cardBreakpoint?: number;
  className?: string;
}

const DEFAULT_COL_WIDTH = 160;

export function DataTable<T>(props: DataTableProps<T>) {
  const { id, caption, data, columns, getRowId, sort, onSortChange, onRowClick, activeRowId, selectable, selection, onSelectionChange } = props;
  const [ref, width] = useElementWidth();
  const cards = width > 0 && width < (props.cardBreakpoint ?? 640);

  const defaultHidden = useMemo(() => columns.filter((c) => c.defaultHidden).map((c) => c.id), [columns]);
  const [hidden, setHidden] = useLocalStorage<string[]>(`nova.table.${id}.hidden`, defaultHidden);
  const [localSort, setLocalSort] = useState<SortingState>(parseSort(sort));
  const [localSelection, setLocalSelection] = useState<RowSelectionState>({});

  const sorting = onSortChange ? parseSort(sort) : localSort;
  const rowSelection: RowSelectionState = selection ? Object.fromEntries(selection.map((k) => [k, true])) : localSelection;
  const columnVisibility: VisibilityState = Object.fromEntries(hidden.map((k) => [k, false]));

  const colDefs = useMemo<ColumnDef<T>[]>(() => {
    const defs: ColumnDef<T>[] = columns.map((c) => ({
      id: c.id,
      header: c.header,
      accessorFn: c.accessor ?? (() => null),
      cell: (ctx) => (c.cell ? c.cell(ctx.row.original) : ((ctx.getValue() as ReactNode) ?? '—')),
      enableSorting: c.sortable ?? !!c.accessor,
      enableHiding: !c.primary && c.hideable !== false,
      sortUndefined: 'last',
      meta: { header: c.header, align: c.align, width: c.width },
    }));
    if (selectable) {
      defs.unshift({
        id: '_select',
        enableSorting: false,
        enableHiding: false,
        meta: { header: '', width: '44px' },
        header: ({ table }) => (
          <Checkbox
            checked={table.getIsAllRowsSelected() ? true : table.getIsSomeRowsSelected() ? 'indeterminate' : false}
            onChange={(v) => table.toggleAllRowsSelected(v)}
            aria-label={t('Select all')}
          />
        ),
        cell: ({ row }) => <Checkbox checked={row.getIsSelected()} onChange={(v) => row.toggleSelected(v)} aria-label={t('Select row')} />,
      });
    }
    return defs;
  }, [columns, selectable]);

  const apply = <S,>(updater: Updater<S>, current: S): S => (typeof updater === 'function' ? (updater as (old: S) => S)(current) : updater);

  // eslint-disable-next-line react-hooks/incompatible-library -- TanStack Table is used as documented
  const table = useReactTable({
    data,
    columns: colDefs,
    getRowId: (row) => getRowId(row),
    state: { sorting, rowSelection, columnVisibility },
    enableRowSelection: !!selectable,
    onSortingChange: (u) => {
      const next = apply(u, sorting);
      if (onSortChange) onSortChange(formatSort(next));
      else setLocalSort(next);
    },
    onRowSelectionChange: (u) => {
      const next = apply(u, rowSelection);
      if (onSelectionChange) onSelectionChange(Object.keys(next).filter((k) => next[k]));
      else setLocalSelection(next);
    },
    onColumnVisibilityChange: (u) => {
      const next = apply(u, columnVisibility);
      setHidden(Object.keys(next).filter((k) => next[k] === false));
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualSorting: !!props.serverSort,
  });

  const selectedIds = Object.keys(rowSelection).filter((k) => rowSelection[k]);
  const visibleCols = columns.filter((c) => !hidden.includes(c.id));
  const minWidth = table.getVisibleLeafColumns().reduce((sum, c) => sum + (parseInt(c.columnDef.meta?.width ?? '', 10) || DEFAULT_COL_WIDTH), 0);

  const exportCsv = () => {
    const rows = table.getRowModel().rows.map((r) => r.original);
    downloadCsv(props.csvName ?? id, toCsv(rows, visibleCols));
  };

  let body: ReactNode;
  if (props.error && !data.length) body = <ErrorState error={props.error} onRetry={props.onRetry} />;
  else if (!props.loading && !data.length) body = props.empty ?? <EmptyState title={t('Nothing to show')} description={t('Try other filters.')} />;
  else if (cards)
    body = (
      <CardList
        table={table}
        caption={caption}
        loading={props.loading}
        onRowClick={onRowClick}
        activeRowId={activeRowId}
        hasMore={props.hasMore}
        onLoadMore={props.onLoadMore}
        primaryId={columns.find((c) => c.primary)?.id}
        secondaryIds={columns.filter((c) => c.secondary).map((c) => c.id)}
      />
    );
  else
    body = (
      <TableView
        table={table}
        caption={caption}
        loading={props.loading}
        onRowClick={onRowClick}
        activeRowId={activeRowId}
        hasMore={props.hasMore}
        onLoadMore={props.onLoadMore}
        minWidth={minWidth}
      />
    );

  return (
    <div ref={ref} className={cx(s.root, cards && s.cardMode, props.className)} data-mode={cards ? 'cards' : 'table'}>
      <div className={s.toolbar}>
        <div className={s.toolbarStart}>
          {selectedIds.length > 0 ? (
            <>
              <span className={s.selectedCount}>{tp(selectedIds.length, '{count} selected', '{count} selected')}</span>
              {props.bulkActions?.(selectedIds)}
            </>
          ) : null}
        </div>
        <div className={s.toolbarEnd}>
          {props.csvName !== undefined && <IconButton icon={Download} label={t('Export CSV')} size="sm" onClick={exportCsv} disabled={!data.length} />}
          {!cards && <ColumnChooser table={table} onReset={() => setHidden(defaultHidden)} />}
        </div>
      </div>
      {body}
    </div>
  );
}
